import { gql } from '@apollo/client';

// ---------------------------------------------------------------------------
// EMPLOYEES
// ---------------------------------------------------------------------------

export const EMPLOYEES_QUERY = gql`
  query Employees($page: Int, $perPage: Int, $search: String, $status: String) {
    employees(page: $page, perPage: $perPage, search: $search, status: $status) {
      data {
        id firstName lastName email employeeNumber status hireDate
        department { id name }
        branch { id name }
        title { id name }
      }
      total page perPage totalPages
    }
  }
`;

export const EMPLOYEE_QUERY = gql`
  query Employee($id: ID!) {
    employee(id: $id) {
      id firstName lastName middleName email employeeNumber status hireDate terminationDate
      department { id name }
      branch { id name }
      title { id name }
      wages { id wageType hourlyRate annualSalary effectiveDate }
      deductions { id name amount percent type }
      accruals { id policyName balance ytdUsed }
    }
  }
`;

export const CREATE_EMPLOYEE_MUTATION = gql`
  mutation CreateEmployee($input: EmployeeInput!) {
    createEmployee(input: $input) { id firstName lastName email employeeNumber }
  }
`;

export const UPDATE_EMPLOYEE_MUTATION = gql`
  mutation UpdateEmployee($id: ID!, $input: EmployeeInput!) {
    updateEmployee(id: $id, input: $input) { id firstName lastName email }
  }
`;

// ---------------------------------------------------------------------------
// PAYROLL
// ---------------------------------------------------------------------------

export const PAYROLL_RUNS_QUERY = gql`
  query PayrollRuns($page: Int, $perPage: Int, $status: String) {
    payrollRuns(page: $page, perPage: $perPage, status: $status) {
      data { id status payPeriodId totalEmployees totalGross totalNet totalDeductions totalEmployerCost isDryRun createdAt createdBy }
      total page perPage
    }
  }
`;

export const PAYROLL_RUN_QUERY = gql`
  query PayrollRun($id: ID!) {
    payrollRun(id: $id) {
      id status payPeriodId totalEmployees totalGross totalNet totalDeductions totalEmployerCost isDryRun errors warnings
      payStubs { id employeeId employeeName grossPay netPay federalTax stateTax ssTax medicareTax }
    }
  }
`;

export const RUN_PAYROLL_MUTATION = gql`
  mutation RunPayroll($input: RunPayrollInput!) {
    runPayroll(input: $input) { runId status totalEmployees totalGross totalNet isDryRun errors warnings }
  }
`;

export const APPROVE_PAYROLL_MUTATION = gql`
  mutation ApprovePayrollRun($id: ID!) {
    approvePayrollRun(id: $id) { id status }
  }
`;

export const PAY_PERIODS_QUERY = gql`
  query PayPeriods($page: Int, $perPage: Int) {
    payPeriods(page: $page, perPage: $perPage) {
      data { id startDate endDate status transactionDate }
      total
    }
  }
`;

// ---------------------------------------------------------------------------
// TIME & ATTENDANCE (Payroll Subgraph — PayrollTimeEntry / PayrollTimesheet)
// Session 3: Rewired from ERP → Payroll (compliance-first, wf-5-0-006)
// ---------------------------------------------------------------------------

export const TIMESHEETS_QUERY = gql`
  query PayrollTimesheets($employeeId: String, $payPeriodStart: String, $status: PayrollTimesheetStatus, $limit: Int) {
    payrollTimesheets(employeeId: $employeeId, payPeriodStart: $payPeriodStart, status: $status, limit: $limit) {
      id tenantId employeeId payPeriodStart payPeriodEnd status
      totalRegularHours totalOvertimeHours totalDoubleTimeHours totalBreakHours totalPtoHours
      notes submittedAt submittedBy approvedBy approvedAt
      rejectedBy rejectedAt rejectionReason createdBy createdAt updatedAt
    }
  }
`;

export const CLOCK_IN_MUTATION = gql`
  mutation PayrollClockIn($input: PayrollClockInInput!) {
    payrollClockIn(input: $input) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status timesheetId jobCode departmentId locationId
      notes createdBy createdAt updatedAt
    }
  }
`;

export const CLOCK_OUT_MUTATION = gql`
  mutation PayrollClockOut($input: PayrollClockOutInput!) {
    payrollClockOut(input: $input) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status notes createdBy createdAt updatedAt
    }
  }
`;

export const WHOS_IN_QUERY = gql`
  query PayrollWhosIn($limit: Int) {
    payrollWhosIn(limit: $limit) {
      employeeId employeeName punchInTime timeEntryId source departmentId locationId
    }
  }
`;

export const TIME_ENTRIES_QUERY = gql`
  query PayrollTimeEntries($employeeId: String, $startDate: String, $endDate: String, $status: PayrollTimeEntryStatus, $limit: Int) {
    payrollTimeEntries(employeeId: $employeeId, startDate: $startDate, endDate: $endDate, status: $status, limit: $limit) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status timesheetId jobCode departmentId locationId
      notes createdBy createdAt updatedAt approvedBy approvedAt
    }
  }
`;

export const SUBMIT_TIME_ENTRY_MUTATION = gql`
  mutation SubmitPayrollTimeEntry($id: ID!) {
    submitPayrollTimeEntry(id: $id) {
      id status updatedAt
    }
  }
`;

export const START_BREAK_MUTATION = gql`
  mutation PayrollStartBreak($input: PayrollBreakInput!) {
    payrollStartBreak(input: $input) {
      id status updatedAt
    }
  }
`;

export const END_BREAK_MUTATION = gql`
  mutation PayrollEndBreak($input: PayrollBreakInput!) {
    payrollEndBreak(input: $input) {
      id status updatedAt
    }
  }
`;

export const PAYROLL_SUBMIT_TIMESHEET_MUTATION = gql`
  mutation SubmitPayrollTimesheet($input: PayrollSubmitTimesheetInput!) {
    submitPayrollTimesheet(input: $input) {
      id status submittedAt submittedBy updatedAt
    }
  }
`;

export const PAYROLL_APPROVE_TIMESHEET_MUTATION = gql`
  mutation ApprovePayrollTimesheet($input: PayrollApproveTimesheetInput!) {
    approvePayrollTimesheet(input: $input) {
      id status approvedBy approvedAt updatedAt
    }
  }
`;

export const PAYROLL_REJECT_TIMESHEET_MUTATION = gql`
  mutation RejectPayrollTimesheet($input: PayrollRejectTimesheetInput!) {
    rejectPayrollTimesheet(input: $input) {
      id status rejectedBy rejectedAt rejectionReason updatedAt
    }
  }
`;

export const EXCEPTIONS_QUERY = gql`
  query Exceptions($page: Int, $perPage: Int, $startDate: String, $endDate: String) {
    exceptions(page: $page, perPage: $perPage, startDate: $startDate, endDate: $endDate) {
      data { id employeeId typeId dateStamp severity status note }
      total
    }
  }
`;

// ---------------------------------------------------------------------------
// SCHEDULES
// ---------------------------------------------------------------------------

export const SCHEDULE_WEEK_QUERY = gql`
  query ScheduleWeek($weekStart: String!) {
    scheduleWeek(weekStart: $weekStart) {
      weekStart weekEnd
      shifts { id employeeId startTime endTime status branchId departmentId }
    }
  }
`;

export const CREATE_SHIFT_MUTATION = gql`
  mutation CreateSchedule($input: ScheduleInput!) {
    createSchedule(input: $input) { id employeeId startTime endTime status }
  }
`;

// ---------------------------------------------------------------------------
// POLICIES
// ---------------------------------------------------------------------------

export const POLICIES_QUERY = gql`
  query Policies($page: Int, $perPage: Int, $policyType: String) {
    policies(page: $page, perPage: $perPage, policyType: $policyType) {
      data { id policyType name description status }
      total
    }
  }
`;

export const HOLIDAYS_QUERY = gql`
  query Holidays {
    holidays { id name date holidayType status }
  }
`;

export const POLICY_DETAIL_QUERY = gql`
  query Policy($id: ID!) {
    policy(id: $id) {
      id originalId version isActive policyType name description status config employeeCount createdAt createdBy
    }
  }
`;

export const CREATE_POLICY_MUTATION = gql`
  mutation CreatePolicy($input: CreatePolicyInput!) {
    createPolicy(input: $input) { id version name }
  }
`;

export const UPDATE_POLICY_MUTATION = gql`
  mutation UpdatePolicy($id: ID!, $input: UpdatePolicyInput!) {
    updatePolicy(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_POLICY_MUTATION = gql`
  mutation DeletePolicy($id: ID!, $reason: String!) {
    deletePolicy(id: $id, reason: $reason) { id isActive version }
  }
`;

// ---------------------------------------------------------------------------
// COMPANY
// ---------------------------------------------------------------------------

export const COMPANY_QUERY = gql`
  query Company {
    company { id name legalName ein address { line1 line2 city state zip country } phone website }
  }
`;

export const BRANCHES_QUERY = gql`
  query Branches {
    branches { id name status address { city state } }
  }
`;

export const DEPARTMENTS_QUERY = gql`
  query Departments {
    departments { id name status }
  }
`;

// ---------------------------------------------------------------------------
// REPORTS
// ---------------------------------------------------------------------------

export const SAVED_REPORTS_QUERY = gql`
  query SavedReports {
    savedReports { id name reportType }
  }
`;

export const GENERATE_REPORT_MUTATION = gql`
  mutation GenerateReport($input: ReportInput!) {
    generateReport(input: $input) { id name status downloadUrl }
  }
`;

// ---------------------------------------------------------------------------
// REQUESTS
// ---------------------------------------------------------------------------

export const REQUESTS_QUERY = gql`
  query Requests($page: Int, $perPage: Int, $status: String) {
    requests(page: $page, perPage: $perPage, status: $status) {
      data { id employeeId requestType status dateStart dateEnd message }
      total
    }
  }
`;

export const CREATE_REQUEST_MUTATION = gql`
  mutation CreateRequest($input: RequestInput!) {
    createRequest(input: $input) { id requestType status }
  }
`;

// ---------------------------------------------------------------------------
// HR
// ---------------------------------------------------------------------------

export const KPIS_QUERY = gql`
  query Kpis {
    kpis { id name description kpiType status }
  }
`;

export const REVIEWS_QUERY = gql`
  query Reviews($page: Int, $perPage: Int) {
    reviews(page: $page, perPage: $perPage) {
      data { id employeeId reviewerId reviewType status overallRating dueDate }
      total
    }
  }
`;

// ---------------------------------------------------------------------------
// NOTIFICATIONS
// ---------------------------------------------------------------------------

export const NOTIFICATIONS_QUERY = gql`
  query Notifications {
    notifications { id title body typeId isRead priority createdAt }
  }
`;

export const MARK_NOTIFICATION_READ_MUTATION = gql`
  mutation MarkNotificationRead($id: ID!) {
    markNotificationRead(id: $id) { id isRead }
  }
`;

// ---------------------------------------------------------------------------
// AUDIT LOG
// ---------------------------------------------------------------------------

export const AUDIT_LOG_QUERY = gql`
  query AuditLog($page: Int, $perPage: Int, $action: String, $userId: String, $startDate: String, $endDate: String) {
    auditLog(page: $page, perPage: $perPage, action: $action, userId: $userId, startDate: $startDate, endDate: $endDate) {
      data { id userId action objectType objectId timestamp }
      total page perPage totalPages
    }
  }
`;

// ---------------------------------------------------------------------------
// PAY PERIODS
// ---------------------------------------------------------------------------

export const CREATE_PAY_PERIOD_MUTATION = gql`
  mutation CreatePayPeriod($input: CreatePayPeriodInput!) {
    createPayPeriod(input: $input) { id startDate endDate payDate frequency status version }
  }
`;

export const TRANSITION_PAY_PERIOD_MUTATION = gql`
  mutation TransitionPayPeriod($id: ID!, $targetState: String!, $reason: String) {
    transitionPayPeriod(id: $id, targetState: $targetState, reason: $reason) { id status version }
  }
`;

// ---------------------------------------------------------------------------
// AMENDMENTS
// ---------------------------------------------------------------------------

export const AMENDMENTS_QUERY = gql`
  query Amendments($page: Int, $perPage: Int, $status: String, $employeeId: ID) {
    amendments(page: $page, perPage: $perPage, status: $status, employeeId: $employeeId) {
      data { id originalId version isActive employeeId employeeName type description amount percent effectiveDate status isRecurring recurrenceCount createdAt }
      total page perPage
    }
  }
`;

export const CREATE_AMENDMENT_MUTATION = gql`
  mutation CreateAmendment($input: CreateAmendmentInput!) {
    createAmendment(input: $input) { id version status }
  }
`;

export const UPDATE_AMENDMENT_MUTATION = gql`
  mutation UpdateAmendment($id: ID!, $input: UpdateAmendmentInput!) {
    updateAmendment(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_AMENDMENT_MUTATION = gql`
  mutation DeleteAmendment($id: ID!, $reason: String!) {
    deleteAmendment(id: $id, reason: $reason) { id isActive version }
  }
`;

// ---------------------------------------------------------------------------
// TIMESHEET VERIFICATION
// ---------------------------------------------------------------------------

export const TIMESHEET_VERIFICATIONS_QUERY = gql`
  query TimesheetVerifications($payPeriodId: ID, $status: String) {
    timesheetVerifications(payPeriodId: $payPeriodId, status: $status) {
      data { id originalId version employeeId employeeName payPeriodId status totalHours regularHours overtimeHours submittedAt approvedAt approvedBy rejectionReason }
      total
    }
  }
`;

export const SUBMIT_TIMESHEET_MUTATION = gql`
  mutation SubmitTimesheet($input: SubmitTimesheetInput!) {
    submitTimesheet(input: $input) { id status version }
  }
`;

export const APPROVE_TIMESHEET_MUTATION = gql`
  mutation ApproveTimesheet($id: ID!) {
    approveTimesheet(id: $id) { id status version approvedAt }
  }
`;

export const REJECT_TIMESHEET_MUTATION = gql`
  mutation RejectTimesheet($id: ID!, $reason: String!) {
    rejectTimesheet(id: $id, reason: $reason) { id status version }
  }
`;

export const BULK_APPROVE_TIMESHEETS_MUTATION = gql`
  mutation BulkApproveTimesheets($ids: [ID!]!) {
    bulkApproveTimesheets(ids: $ids) { approved rejected errors }
  }
`;

// ---------------------------------------------------------------------------
// ACCRUAL POLICIES
// ---------------------------------------------------------------------------

export const ACCRUAL_POLICIES_QUERY = gql`
  query AccrualPolicies($page: Int, $perPage: Int) {
    accrualPolicies(page: $page, perPage: $perPage) {
      data { id originalId version isActive name policyType accrualFrequency maxBalanceHours carryoverMaxHours waitingPeriodDays milestones { yearsOfService annualAccrualHours } employeeCount status createdAt }
      total
    }
  }
`;

export const CREATE_ACCRUAL_POLICY_MUTATION = gql`
  mutation CreateAccrualPolicy($input: CreateAccrualPolicyInput!) {
    createAccrualPolicy(input: $input) { id version name }
  }
`;

export const UPDATE_ACCRUAL_POLICY_MUTATION = gql`
  mutation UpdateAccrualPolicy($id: ID!, $input: UpdateAccrualPolicyInput!) {
    updateAccrualPolicy(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_ACCRUAL_POLICY_MUTATION = gql`
  mutation DeleteAccrualPolicy($id: ID!, $reason: String!) {
    deleteAccrualPolicy(id: $id, reason: $reason) { id isActive version }
  }
`;

// ---------------------------------------------------------------------------
// BANK ACCOUNTS
// ---------------------------------------------------------------------------

export const BANK_ACCOUNTS_QUERY = gql`
  query BankAccounts($employeeId: ID!) {
    bankAccounts(employeeId: $employeeId) {
      id originalId version isActive employeeId accountType maskedAccountNumber maskedRoutingNumber isPrimary depositType depositAmount depositPercent createdAt createdBy
    }
  }
`;

export const CREATE_BANK_ACCOUNT_MUTATION = gql`
  mutation CreateBankAccount($input: CreateBankAccountInput!) {
    createBankAccount(input: $input) { id version maskedAccountNumber }
  }
`;

export const UPDATE_BANK_ACCOUNT_MUTATION = gql`
  mutation UpdateBankAccount($id: ID!, $input: UpdateBankAccountInput!) {
    updateBankAccount(id: $id, input: $input) { id originalId version maskedAccountNumber }
  }
`;

export const DELETE_BANK_ACCOUNT_MUTATION = gql`
  mutation DeleteBankAccount($id: ID!, $reason: String!) {
    deleteBankAccount(id: $id, reason: $reason) { id isActive version }
  }
`;

export const BANK_ACCOUNT_HISTORY_QUERY = gql`
  query BankAccountHistory($originalId: ID!) {
    bankAccountHistory(originalId: $originalId) {
      id version isActive accountType maskedAccountNumber isPrimary createdAt createdBy changeReason
    }
  }
`;

// ---------------------------------------------------------------------------
// IMPORT/EXPORT
// ---------------------------------------------------------------------------

export const IMPORT_JOBS_QUERY = gql`
  query ImportJobs($page: Int, $perPage: Int, $status: String) {
    importJobs(page: $page, perPage: $perPage, status: $status) {
      data { id fileName entityType status totalRows processedRows errorRows createdAt completedAt createdBy errors }
      total
    }
  }
`;

export const START_IMPORT_MUTATION = gql`
  mutation StartImport($input: StartImportInput!) {
    startImport(input: $input) { id status fileName }
  }
`;

export const EXPORT_DATA_MUTATION = gql`
  mutation ExportData($input: ExportDataInput!) {
    exportData(input: $input) { id status downloadUrl }
  }
`;

export const EXPORT_JOBS_QUERY = gql`
  query ExportJobs($page: Int, $perPage: Int) {
    exportJobs(page: $page, perPage: $perPage) {
      data { id entityType format status downloadUrl createdAt createdBy }
      total
    }
  }
`;

// ---------------------------------------------------------------------------
// RECURRING SCHEDULES
// ---------------------------------------------------------------------------

export const RECURRING_SCHEDULES_QUERY = gql`
  query RecurringSchedules($page: Int, $perPage: Int) {
    recurringSchedules(page: $page, perPage: $perPage) {
      data { id originalId version isActive name description templateId templateName employeeIds startDate endDate frequency status createdAt }
      total
    }
  }
`;

export const CREATE_RECURRING_SCHEDULE_MUTATION = gql`
  mutation CreateRecurringSchedule($input: CreateRecurringScheduleInput!) {
    createRecurringSchedule(input: $input) { id version name }
  }
`;

export const UPDATE_RECURRING_SCHEDULE_MUTATION = gql`
  mutation UpdateRecurringSchedule($id: ID!, $input: UpdateRecurringScheduleInput!) {
    updateRecurringSchedule(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_RECURRING_SCHEDULE_MUTATION = gql`
  mutation DeleteRecurringSchedule($id: ID!, $reason: String!) {
    deleteRecurringSchedule(id: $id, reason: $reason) { id isActive version }
  }
`;

// ---------------------------------------------------------------------------
// DOCUMENTS
// ---------------------------------------------------------------------------

export const DOCUMENTS_QUERY = gql`
  query Documents($page: Int, $perPage: Int, $category: String, $employeeId: ID, $search: String, $folderId: ID) {
    documents(page: $page, perPage: $perPage, category: $category, employeeId: $employeeId, search: $search, folderId: $folderId) {
      data { id originalId version isActive name fileName fileSize mimeType category description employeeId employeeName tags storageKey downloadUrl expiresAt requiresAcknowledgment acknowledgedCount totalRecipients uploadedBy uploadedAt createdAt }
      total page perPage
    }
  }
`;

export const DOCUMENT_QUERY = gql`
  query Document($id: ID!) {
    document(id: $id) {
      id originalId version isActive name fileName fileSize mimeType category description employeeId employeeName tags storageKey downloadUrl expiresAt requiresAcknowledgment acknowledgedBy acknowledgedCount totalRecipients uploadedBy uploadedAt createdAt
    }
  }
`;

export const UPLOAD_DOCUMENT_MUTATION = gql`
  mutation UploadDocument($input: UploadDocumentInput!) {
    uploadDocument(input: $input) { id version name fileName storageKey }
  }
`;

export const UPDATE_DOCUMENT_MUTATION = gql`
  mutation UpdateDocument($id: ID!, $input: UpdateDocumentInput!) {
    updateDocument(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_DOCUMENT_MUTATION = gql`
  mutation DeleteDocument($id: ID!, $reason: String!) {
    deleteDocument(id: $id, reason: $reason) { id isActive version }
  }
`;

export const DOCUMENT_FOLDERS_QUERY = gql`
  query DocumentFolders($parentId: ID) {
    documentFolders(parentId: $parentId) { id name parentId documentCount path }
  }
`;

export const CREATE_DOCUMENT_FOLDER_MUTATION = gql`
  mutation CreateDocumentFolder($input: CreateDocumentFolderInput!) {
    createDocumentFolder(input: $input) { id name path }
  }
`;

export const ACKNOWLEDGE_DOCUMENT_MUTATION = gql`
  mutation AcknowledgeDocument($documentId: ID!) {
    acknowledgeDocument(documentId: $documentId) { documentId employeeId acknowledgedAt }
  }
`;

export const DOCUMENT_ACKNOWLEDGMENTS_QUERY = gql`
  query DocumentAcknowledgments($documentId: ID!) {
    documentAcknowledgments(documentId: $documentId) { documentId employeeId employeeName acknowledgedAt ipAddress }
  }
`;

export const DOCUMENT_VERSIONS_QUERY = gql`
  query DocumentVersions($originalId: ID!) {
    documentVersions(originalId: $originalId) { id version isActive name fileName fileSize uploadedBy uploadedAt createdAt }
  }
`;

// ---------------------------------------------------------------------------
// GEO FENCING
// ---------------------------------------------------------------------------

export const GEO_FENCES_QUERY = gql`
  query GeoFences($page: Int, $perPage: Int) {
    geoFences(page: $page, perPage: $perPage) {
      data { id originalId version isActive name description type latitude longitude radius polygon { lat lng } address branchId branchName enforcementMode allowedActions employeeCount createdAt }
      total
    }
  }
`;

export const GEO_FENCE_QUERY = gql`
  query GeoFence($id: ID!) {
    geoFence(id: $id) {
      id originalId version isActive name description type latitude longitude radius polygon { lat lng } address branchId branchName enforcementMode allowedActions employeeCount createdAt
    }
  }
`;

export const CREATE_GEO_FENCE_MUTATION = gql`
  mutation CreateGeoFence($input: CreateGeoFenceInput!) {
    createGeoFence(input: $input) { id version name }
  }
`;

export const UPDATE_GEO_FENCE_MUTATION = gql`
  mutation UpdateGeoFence($id: ID!, $input: UpdateGeoFenceInput!) {
    updateGeoFence(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_GEO_FENCE_MUTATION = gql`
  mutation DeleteGeoFence($id: ID!, $reason: String!) {
    deleteGeoFence(id: $id, reason: $reason) { id isActive version }
  }
`;

export const GEO_FENCE_VIOLATIONS_QUERY = gql`
  query GeoFenceViolations($geoFenceId: ID, $employeeId: ID, $status: String, $startDate: String, $endDate: String, $page: Int, $perPage: Int) {
    geoFenceViolations(geoFenceId: $geoFenceId, employeeId: $employeeId, status: $status, startDate: $startDate, endDate: $endDate, page: $page, perPage: $perPage) {
      data { id employeeId employeeName geoFenceId geoFenceName action latitude longitude distanceFromFence timestamp status reviewedBy reviewNote }
      total
      summary { totalViolations pendingCount approvedCount rejectedCount }
    }
  }
`;

export const REVIEW_GEO_FENCE_VIOLATION_MUTATION = gql`
  mutation ReviewGeoFenceViolation($id: ID!, $input: ReviewViolationInput!) {
    reviewGeoFenceViolation(id: $id, input: $input) { id status reviewedBy reviewNote }
  }
`;

export const BULK_REVIEW_VIOLATIONS_MUTATION = gql`
  mutation BulkReviewViolations($ids: [ID!]!, $input: ReviewViolationInput!) {
    bulkReviewViolations(ids: $ids, input: $input) { approved rejected errors }
  }
`;

export const EMPLOYEE_LOCATIONS_QUERY = gql`
  query EmployeeLocations {
    employeeLocations { employeeId employeeName latitude longitude accuracy timestamp insideFence nearestFence }
  }
`;

// ---------------------------------------------------------------------------
// INVOICES
// ---------------------------------------------------------------------------

export const INVOICES_QUERY = gql`
  query Invoices($page: Int, $perPage: Int, $status: String, $clientId: String) {
    invoices(page: $page, perPage: $perPage, status: $status, clientId: $clientId) {
      data { id originalId version isActive invoiceNumber clientId clientName status issueDate dueDate lineItems { description quantity unitPrice amount jobId jobName } subtotal taxRate taxAmount total amountPaid amountDue notes terms currency createdAt createdBy }
      total page perPage
    }
  }
`;

export const INVOICE_QUERY = gql`
  query Invoice($id: ID!) {
    invoice(id: $id) {
      id originalId version isActive invoiceNumber clientId clientName status issueDate dueDate lineItems { description quantity unitPrice amount jobId jobName } subtotal taxRate taxAmount total amountPaid amountDue notes terms currency createdAt createdBy
    }
  }
`;

export const INVOICE_PAYMENTS_QUERY = gql`
  query InvoicePayments($invoiceId: ID!) {
    invoicePayments(invoiceId: $invoiceId) { id invoiceId amount paymentDate method reference notes createdAt }
  }
`;

export const INVOICE_SUMMARY_QUERY = gql`
  query InvoiceSummary {
    invoiceSummary { totalOutstanding totalOverdue paidThisMonth totalRevenueYtd }
  }
`;

export const CREATE_INVOICE_MUTATION = gql`
  mutation CreateInvoice($input: CreateInvoiceInput!) {
    createInvoice(input: $input) { id version invoiceNumber status }
  }
`;

export const UPDATE_INVOICE_MUTATION = gql`
  mutation UpdateInvoice($id: ID!, $input: UpdateInvoiceInput!) {
    updateInvoice(id: $id, input: $input) { id originalId version }
  }
`;

export const SEND_INVOICE_MUTATION = gql`
  mutation SendInvoice($id: ID!) {
    sendInvoice(id: $id) { id status version }
  }
`;

export const RECORD_PAYMENT_MUTATION = gql`
  mutation RecordPayment($input: RecordPaymentInput!) {
    recordPayment(input: $input) { id invoiceId amount paymentDate method }
  }
`;

export const VOID_INVOICE_MUTATION = gql`
  mutation VoidInvoice($id: ID!, $reason: String!) {
    voidInvoice(id: $id, reason: $reason) { id status version }
  }
`;

// ---------------------------------------------------------------------------
// EXPENSES
// ---------------------------------------------------------------------------

export const EXPENSES_QUERY = gql`
  query Expenses($page: Int, $perPage: Int, $status: String, $employeeId: ID, $category: String) {
    expenses(page: $page, perPage: $perPage, status: $status, employeeId: $employeeId, category: $category) {
      data { id originalId version isActive employeeId employeeName category description amount currency receiptUrl vendor date status approvedBy approvedAt reimbursedAt policyViolations reportId createdAt createdBy }
      total page perPage
    }
  }
`;

export const EXPENSE_QUERY = gql`
  query Expense($id: ID!) {
    expense(id: $id) {
      id originalId version isActive employeeId employeeName category description amount currency receiptUrl vendor date status approvedBy approvedAt reimbursedAt policyViolations reportId createdAt createdBy
    }
  }
`;

export const CREATE_EXPENSE_MUTATION = gql`
  mutation CreateExpense($input: CreateExpenseInput!) {
    createExpense(input: $input) { id version status }
  }
`;

export const UPDATE_EXPENSE_MUTATION = gql`
  mutation UpdateExpense($id: ID!, $input: UpdateExpenseInput!) {
    updateExpense(id: $id, input: $input) { id originalId version }
  }
`;

export const SUBMIT_EXPENSE_MUTATION = gql`
  mutation SubmitExpense($id: ID!) {
    submitExpense(id: $id) { id status version }
  }
`;

export const APPROVE_EXPENSE_MUTATION = gql`
  mutation ApproveExpense($id: ID!) {
    approveExpense(id: $id) { id status version approvedAt }
  }
`;

export const REJECT_EXPENSE_MUTATION = gql`
  mutation RejectExpense($id: ID!, $reason: String!) {
    rejectExpense(id: $id, reason: $reason) { id status version }
  }
`;

export const EXPENSE_REPORTS_QUERY = gql`
  query ExpenseReports($page: Int, $perPage: Int, $employeeId: ID, $status: String) {
    expenseReports(page: $page, perPage: $perPage, employeeId: $employeeId, status: $status) {
      data { id originalId version isActive employeeId employeeName title period expenses { id category description amount vendor date status policyViolations } totalAmount status submittedAt approvedAt approvedBy rejectionReason createdAt createdBy }
      total page perPage
    }
  }
`;

export const EXPENSE_REPORT_QUERY = gql`
  query ExpenseReport($id: ID!) {
    expenseReport(id: $id) {
      id originalId version isActive employeeId employeeName title period expenses { id category description amount currency vendor date status receiptUrl policyViolations } totalAmount status submittedAt approvedAt approvedBy rejectionReason createdAt createdBy
    }
  }
`;

export const CREATE_EXPENSE_REPORT_MUTATION = gql`
  mutation CreateExpenseReport($input: CreateExpenseReportInput!) {
    createExpenseReport(input: $input) { id version title }
  }
`;

export const SUBMIT_EXPENSE_REPORT_MUTATION = gql`
  mutation SubmitExpenseReport($id: ID!) {
    submitExpenseReport(id: $id) { id status version submittedAt }
  }
`;

export const APPROVE_EXPENSE_REPORT_MUTATION = gql`
  mutation ApproveExpenseReport($id: ID!) {
    approveExpenseReport(id: $id) { id status version approvedAt }
  }
`;

export const REJECT_EXPENSE_REPORT_MUTATION = gql`
  mutation RejectExpenseReport($id: ID!, $reason: String!) {
    rejectExpenseReport(id: $id, reason: $reason) { id status version }
  }
`;

export const ADD_EXPENSE_TO_REPORT_MUTATION = gql`
  mutation AddExpenseToReport($reportId: ID!, $expenseId: ID!) {
    addExpenseToReport(reportId: $reportId, expenseId: $expenseId) { id version totalAmount }
  }
`;

// ---------------------------------------------------------------------------
// SCHEDULE TEMPLATES
// ---------------------------------------------------------------------------

export const SCHEDULE_TEMPLATES_QUERY = gql`
  query ScheduleTemplates {
    scheduleTemplates {
      id
      name
      description
      shiftCount
      shifts {
        dayOfWeek
        startTime
        endTime
        position
      }
      isDefault
      createdAt
    }
  }
`;

// ---------------------------------------------------------------------------
// SETTINGS / ACCOUNT
// ---------------------------------------------------------------------------

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      success
    }
  }
`;

// ---------------------------------------------------------------------------
// REQUEST ACTIONS
// ---------------------------------------------------------------------------

export const APPROVE_REQUEST_MUTATION = gql`
  mutation ApproveRequest($id: ID!) {
    approveRequest(id: $id) { id status }
  }
`;

export const DENY_REQUEST_MUTATION = gql`
  mutation DenyRequest($id: ID!) {
    denyRequest(id: $id) { id status }
  }
`;

// ---------------------------------------------------------------------------
// PAY STUBS
// ---------------------------------------------------------------------------

export const PAY_STUB_DETAIL_QUERY = gql`
  query PayStubDetail($id: ID!) {
    payStub(id: $id) {
      id
      employeeId
      employeeName
      employeeNumber
      payPeriod {
        id
        startDate
        endDate
        payDate
      }
      earnings {
        type
        description
        hours
        rate
        amount
        ytdAmount
      }
      taxes {
        type
        description
        amount
        ytdAmount
        isEmployer
      }
      deductions {
        type
        description
        amount
        ytdAmount
        preTax
      }
      grossPay
      netPay
      totalTaxes
      totalDeductions
      totalEmployerTaxes
      regularHours
      overtimeHours
      totalHours
      ytdGross
      ytdNet
      ytdTaxes
      ytdDeductions
    }
  }
`;

// ---------------------------------------------------------------------------
// QUALIFICATIONS
// ---------------------------------------------------------------------------

export const QUALIFICATIONS_QUERY = gql`
  query Qualifications($page: Int, $perPage: Int, $type: String) {
    qualifications(page: $page, perPage: $perPage, type: $type) {
      data {
        id
        employeeId
        employeeName
        name
        type
        issueDate
        expiryDate
        status
      }
      total
    }
  }
`;

// ---------------------------------------------------------------------------
// REVIEW DETAIL
// ---------------------------------------------------------------------------

export const REVIEW_DETAIL_QUERY = gql`
  query Review($id: ID!) {
    review(id: $id) {
      id
      employeeId
      employeeName
      reviewerId
      reviewerName
      reviewType
      status
      overallRating
      dueDate
      completedDate
      comments
      ratings {
        category
        score
        maxScore
        comment
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// REPORT RESULT
// ---------------------------------------------------------------------------

export const REPORT_RESULT_QUERY = gql`
  query ReportResult($id: ID!) {
    reportResult(id: $id) {
      id
      name
      reportType
      status
      createdAt
      completedAt
      downloadUrl
      parameters
      summary {
        key
        label
        value
      }
      columns {
        key
        label
      }
      rows
    }
  }
`;

// ---------------------------------------------------------------------------
// COMPANY MUTATIONS
// ---------------------------------------------------------------------------

export const UPDATE_COMPANY_MUTATION = gql`
  mutation UpdateCompany($input: CompanyInput!) {
    updateCompany(input: $input) {
      id
      name
      legalName
    }
  }
`;

// ---------------------------------------------------------------------------
// DEPARTMENTS (Append-Only)
// ---------------------------------------------------------------------------

export const CREATE_DEPARTMENT_MUTATION = gql`
  mutation CreateDepartment($input: CreateDepartmentInput!) {
    createDepartment(input: $input) {
      id
      version
      name
      status
      parentDepartmentId
    }
  }
`;

// ---------------------------------------------------------------------------
// BRANCHES (Append-Only)
// ---------------------------------------------------------------------------

export const CREATE_BRANCH_MUTATION = gql`
  mutation CreateBranch($input: CreateBranchInput!) {
    createBranch(input: $input) {
      id
      version
      name
      status
      address {
        line1
        line2
        city
        state
        zip
        country
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// JOB COSTING
// ---------------------------------------------------------------------------

export const JOBS_QUERY = gql`
  query Jobs($status: String, $page: Int, $perPage: Int) {
    jobs(status: $status, page: $page, perPage: $perPage) {
      data {
        id originalId version isActive name code description clientName status
        budgetAmount actualCost startDate endDate managerId managerName
        departmentId departmentName createdAt
      }
      total page perPage
    }
  }
`;

export const JOB_DETAIL_QUERY = gql`
  query Job($id: ID!) {
    job(id: $id) {
      id originalId version isActive name code description clientName status
      budgetAmount actualCost startDate endDate managerId managerName
      departmentId departmentName createdAt
    }
  }
`;

export const CREATE_JOB_MUTATION = gql`
  mutation CreateJob($input: CreateJobInput!) {
    createJob(input: $input) { id version status }
  }
`;

export const UPDATE_JOB_MUTATION = gql`
  mutation UpdateJob($id: ID!, $input: UpdateJobInput!) {
    updateJob(id: $id, input: $input) { id originalId version }
  }
`;

export const DELETE_JOB_MUTATION = gql`
  mutation DeleteJob($id: ID!, $reason: String!) {
    deleteJob(id: $id, reason: $reason) { id isActive version }
  }
`;

export const JOB_COST_ENTRIES_QUERY = gql`
  query JobCostEntries($jobId: ID!, $page: Int, $perPage: Int) {
    jobCostEntries(jobId: $jobId, page: $page, perPage: $perPage) {
      data {
        id jobId jobName employeeId employeeName date hours laborCost
        costType description payrollRunId createdAt
      }
      total page perPage
    }
  }
`;

export const CREATE_JOB_COST_ENTRY_MUTATION = gql`
  mutation CreateJobCostEntry($input: CreateJobCostEntryInput!) {
    createJobCostEntry(input: $input) { id jobId costType }
  }
`;

export const JOB_BUDGET_SUMMARY_QUERY = gql`
  query JobBudgetSummary($jobId: ID!) {
    jobBudgetSummary(jobId: $jobId) {
      jobId budgetAmount totalLabor totalMaterial totalEquipment
      totalOverhead totalOther totalCost remainingBudget percentUsed
    }
  }
`;

export const JOB_COST_REPORT_QUERY = gql`
  query JobCostReport($startDate: String!, $endDate: String!, $jobIds: [ID!], $departmentId: String) {
    jobCostReport(startDate: $startDate, endDate: $endDate, jobIds: $jobIds, departmentId: $departmentId) {
      jobs {
        jobId jobName jobCode budgetAmount totalLabor totalMaterial
        totalEquipment totalOverhead totalOther totalCost variance
      }
      grandTotalBudget grandTotalCost grandTotalVariance
    }
  }
`;

export const EXPORT_JOB_COST_REPORT_MUTATION = gql`
  mutation ExportJobCostReport($input: ExportJobCostReportInput!) {
    exportJobCostReport(input: $input) { downloadUrl format }
  }
`;

// ---------------------------------------------------------------------------
// RECRUITMENT
// ---------------------------------------------------------------------------

const JOB_POSTING_FRAGMENT = gql`
  fragment JobPostingFields on JobPosting {
    id originalId version isActive title department location type description
    requirements preferredQualifications salaryMin salaryMax currency status
    hiringManagerId hiringManagerName applicantCount postedAt closingDate createdAt
  }
`;

const APPLICANT_FRAGMENT = gql`
  fragment ApplicantFields on Applicant {
    id firstName lastName email phone resumeUrl coverLetterUrl source status
    jobPostingId jobTitle currentStage rating
    notes { id authorId authorName content createdAt }
    appliedAt updatedAt
  }
`;

const INTERVIEW_FRAGMENT = gql`
  fragment InterviewFields on Interview {
    id applicantId applicantName interviewerId interviewerName scheduledAt
    duration type status rating feedback location
  }
`;

const ONBOARDING_FRAGMENT = gql`
  fragment OnboardingFields on OnboardingChecklist {
    id employeeId employeeName templateId
    items { id title description category assignedTo dueDate completedAt required status }
    completedCount totalCount startDate targetCompletionDate status
  }
`;

export const JOB_POSTINGS_QUERY = gql`
  query JobPostings($status: String, $department: String) {
    jobPostings(status: $status, department: $department) { ...JobPostingFields }
  }
  ${JOB_POSTING_FRAGMENT}
`;

export const JOB_POSTING_QUERY = gql`
  query JobPosting($id: ID!) {
    jobPosting(id: $id) { ...JobPostingFields }
  }
  ${JOB_POSTING_FRAGMENT}
`;

export const CREATE_JOB_POSTING_MUTATION = gql`
  mutation CreateJobPosting($input: CreateJobPostingInput!) {
    createJobPosting(input: $input) { ...JobPostingFields }
  }
  ${JOB_POSTING_FRAGMENT}
`;

export const UPDATE_JOB_POSTING_MUTATION = gql`
  mutation UpdateJobPosting($id: ID!, $input: UpdateJobPostingInput!) {
    updateJobPosting(id: $id, input: $input) { ...JobPostingFields }
  }
  ${JOB_POSTING_FRAGMENT}
`;

export const CLOSE_JOB_POSTING_MUTATION = gql`
  mutation CloseJobPosting($id: ID!, $reason: String!) {
    closeJobPosting(id: $id, reason: $reason) { ...JobPostingFields }
  }
  ${JOB_POSTING_FRAGMENT}
`;

export const APPLICANTS_QUERY = gql`
  query Applicants($jobPostingId: ID, $status: String) {
    applicants(jobPostingId: $jobPostingId, status: $status) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const APPLICANT_QUERY = gql`
  query Applicant($id: ID!) {
    applicant(id: $id) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const CREATE_APPLICANT_MUTATION = gql`
  mutation CreateApplicant($input: CreateApplicantInput!) {
    createApplicant(input: $input) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const UPDATE_APPLICANT_STATUS_MUTATION = gql`
  mutation UpdateApplicantStatus($id: ID!, $status: String!) {
    updateApplicantStatus(id: $id, status: $status) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const ADD_APPLICANT_NOTE_MUTATION = gql`
  mutation AddApplicantNote($id: ID!, $content: String!) {
    addApplicantNote(id: $id, content: $content) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const RATE_APPLICANT_MUTATION = gql`
  mutation RateApplicant($id: ID!, $rating: Int!) {
    rateApplicant(id: $id, rating: $rating) { ...ApplicantFields }
  }
  ${APPLICANT_FRAGMENT}
`;

export const INTERVIEWS_QUERY = gql`
  query Interviews($applicantId: ID) {
    interviews(applicantId: $applicantId) { ...InterviewFields }
  }
  ${INTERVIEW_FRAGMENT}
`;

export const SCHEDULE_INTERVIEW_MUTATION = gql`
  mutation ScheduleInterview($input: ScheduleInterviewInput!) {
    scheduleInterview(input: $input) { ...InterviewFields }
  }
  ${INTERVIEW_FRAGMENT}
`;

export const CANCEL_INTERVIEW_MUTATION = gql`
  mutation CancelInterview($id: ID!) {
    cancelInterview(id: $id) { ...InterviewFields }
  }
  ${INTERVIEW_FRAGMENT}
`;

export const SUBMIT_INTERVIEW_FEEDBACK_MUTATION = gql`
  mutation SubmitInterviewFeedback($input: InterviewFeedbackInput!) {
    submitInterviewFeedback(input: $input) { ...InterviewFields }
  }
  ${INTERVIEW_FRAGMENT}
`;

// ---------------------------------------------------------------------------
// LEAVE BALANCES
// ---------------------------------------------------------------------------

export const LEAVE_BALANCES_QUERY = gql`
  query LeaveBalances($employeeId: ID) {
    leaveBalances(employeeId: $employeeId) {
      id employeeId employeeName leaveType policyId policyName
      accrued used pending available carryover maxAccrual accrualRate
      accrualPeriod nextAccrualDate yearStart yearEnd
    }
  }
`;

export const LEAVE_TRANSACTIONS_QUERY = gql`
  query LeaveTransactions($employeeId: ID!, $leaveType: String, $startDate: String, $endDate: String) {
    leaveTransactions(employeeId: $employeeId, leaveType: $leaveType, startDate: $startDate, endDate: $endDate) {
      id employeeId leaveType transactionType hours balanceAfter
      date description requestId approvedBy createdAt
    }
  }
`;

export const ADJUST_LEAVE_BALANCE_MUTATION = gql`
  mutation AdjustLeaveBalance($input: AdjustLeaveBalanceInput!) {
    adjustLeaveBalance(input: $input) {
      id leaveType transactionType hours balanceAfter description
    }
  }
`;

export const TEAM_LEAVE_SUMMARY_QUERY = gql`
  query TeamLeaveSummary($departmentId: ID, $date: String) {
    teamLeaveSummary(departmentId: $departmentId, date: $date) {
      employeeId employeeName department
      balances { leaveType available used pending }
    }
  }
`;

// ---------------------------------------------------------------------------
// GPS PUNCH (Payroll TimeEntry + Core Event for GPS audit trail)
// Session 3: Rewired from ERP → Payroll. GPS data is now first-class on
// PayrollPunchRecord (lat/lng/accuracy fields) instead of JSON-in-comments.
// ---------------------------------------------------------------------------

export const ENHANCED_PUNCHES_QUERY = gql`
  query PayrollPunchRecords($employeeId: String, $limit: Int) {
    payrollPunchRecords(employeeId: $employeeId, limit: $limit) {
      id tenantId employeeId punchType timestamp source
      latitude longitude accuracy deviceInfo timeEntryId notes createdBy createdAt
    }
  }
`;

export const GPS_PUNCH_IN_MUTATION = gql`
  mutation GpsPayrollClockIn($input: PayrollClockInInput!) {
    payrollClockIn(input: $input) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status notes createdBy createdAt updatedAt
    }
  }
`;

export const GPS_PUNCH_OUT_MUTATION = gql`
  mutation GpsPayrollClockOut($input: PayrollClockOutInput!) {
    payrollClockOut(input: $input) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status notes createdBy createdAt updatedAt
    }
  }
`;

export const GPS_EVENT_MUTATION = gql`
  mutation CreateGpsEvent($tenantId: String!, $input: GqlCreateEventInput!) {
    createEvent(tenantId: $tenantId, input: $input) {
      id eventName eventType
    }
  }
`;

export const PUNCH_POLICY_QUERY = gql`
  query PunchPolicy {
    punchPolicy {
      requireGps requireBiometric allowWebPunch allowMobilePunch
      geoFenceEnforcement photoOnPunch ipRestriction allowedIpRanges
      roundingRule gracePeriodMinutes
    }
  }
`;

export const UPDATE_PUNCH_POLICY_MUTATION = gql`
  mutation UpdatePunchPolicy($input: PunchPolicyInput!) {
    updatePunchPolicy(input: $input) {
      requireGps requireBiometric allowWebPunch allowMobilePunch
      geoFenceEnforcement roundingRule gracePeriodMinutes
    }
  }
`;

export const OVERRIDE_PUNCH_MUTATION = gql`
  mutation OverridePunch($punchId: ID!, $reason: String!) {
    overridePunch(punchId: $punchId, reason: $reason) { id status flagReason }
  }
`;

// ---------------------------------------------------------------------------
// ONBOARDING
// ---------------------------------------------------------------------------

export const ONBOARDING_CHECKLISTS_QUERY = gql`
  query OnboardingChecklists($employeeId: ID) {
    onboardingChecklists(employeeId: $employeeId) {
      id employeeId employeeName templateId
      items { id title description category assignedTo dueDate completedAt required status }
      completedCount totalCount startDate targetCompletionDate status
    }
  }
`;

export const ONBOARDING_CHECKLIST_QUERY = gql`
  query OnboardingChecklist($id: ID!) {
    onboardingChecklist(id: $id) {
      id employeeId employeeName templateId
      items { id title description category assignedTo dueDate completedAt required status }
      completedCount totalCount startDate targetCompletionDate status
    }
  }
`;

export const CREATE_ONBOARDING_CHECKLIST_MUTATION = gql`
  mutation CreateOnboardingChecklist($input: CreateOnboardingChecklistInput!) {
    createOnboardingChecklist(input: $input) {
      id employeeId employeeName templateId completedCount totalCount status
    }
  }
`;

export const COMPLETE_ONBOARDING_ITEM_MUTATION = gql`
  mutation CompleteOnboardingItem($checklistId: ID!, $itemId: ID!) {
    completeOnboardingItem(checklistId: $checklistId, itemId: $itemId) {
      id completedCount totalCount status
    }
  }
`;

export const SKIP_ONBOARDING_ITEM_MUTATION = gql`
  mutation SkipOnboardingItem($checklistId: ID!, $itemId: ID!) {
    skipOnboardingItem(checklistId: $checklistId, itemId: $itemId) {
      id completedCount totalCount status
    }
  }
`;

export const ADD_ONBOARDING_ITEM_MUTATION = gql`
  mutation AddOnboardingItem($input: AddOnboardingItemInput!) {
    addOnboardingItem(input: $input) {
      id completedCount totalCount status
    }
  }
`;
